import { defineStore } from "pinia";
import StoreElement from "@/model/Elements/StoreElement";
import DatabaseElement from "@/model/Elements/DatabaseElement";
import { ProfilingChartType } from "@/components/home/ProfilingChartType";
import { getStoreManager } from "@/firebase/StoreManager";
import Store from "@/model/Store";
import Task from "@/model/Task";
import { SnapshotListener } from "@/utils/SnapshotListener";
import {
  collection,
  DocumentReference,
  query,
  where,
} from "firebase/firestore";
import * as Firebase from "@/firebase/Firebase";
import * as ModuleManager from "@/firebase/ModuleManager";
import { list } from "pdfkit";
import Employee from "@/model/Employees/Employee";
import { getEmployeeManager } from "@/firebase/EmployeeManager";
import * as TaskManager from "@/firebase/TaskManager";
import { getElementManager } from "@/firebase/ElementManager";
import { ModuleEnum } from "@/model/ModuleEnum";
import TemperatureResult from "@/model/TaskResults/TemperatureResult";
import EmployeeRole from "@/model/Employees/EmployeeRole";
import { getDatabaseManager } from "@/firebase/DatabaseManager";
import { ElementTemperatureStateEnum } from "@/components/home/ElementTemperatureStateEnum";
import OilResult from "@/model/TaskResults/OilResult";
import Communication from "@/model/Communication/Communication";
import { getCommunicationManager } from "@/firebase/CommunicationManager";
import CommunicationResult from "@/model/Communication/CommunicationResult";
import TrainingResult from "@/model/Trainings/TrainingResult";
import Training from "@/model/Trainings/Training";
import { getTrainingManager } from "@/firebase/TrainingManager";
import { getState } from "../AppState";
import EmployeePunchCard from "@/model/Employees/EmployeePunchCard";
import * as DateUtils from "@/utils/DateUtils";
import PunchCardPair from "@/model/Employees/PunchCardPair";
import PunchCardEntry from "@/model/Employees/PunchCardEntry";
import { Role } from "@/model/User";
import Database from "@/model/Database";
import { getAuditManager } from "@/firebase/AuditManager";

interface HomeState {
  stores: Store[];
  databases: Database[];
  selectedStore: Store | null;
  selectedDatabase: Database | null;
  tasksOfDay: Map<Store, SnapshotListener<Task>>;
  loading: boolean;
  employees: Employee[];
  employeeRoles: EmployeeRole[];
  deliveryElements: Map<StoreElement, ElementTemperatureStateEnum>;
  fridgeElements: Map<StoreElement, ElementTemperatureStateEnum>;
  oilElements: Map<StoreElement, OilResult>;
  communications: Communication[];
  communicationResults: Map<Communication, CommunicationResult[]>;
  trainings: Training[];
  trainingResults: Map<Training, TrainingResult[]>;

  punchCards: Map<string, EmployeePunchCard>;
}

export const getHomeState = defineStore("homeState", {
  state: (): HomeState => {
    return {
      stores: [],
      databases: [],
      selectedStore: null,
      selectedDatabase: null,
      tasksOfDay: new Map<Store, SnapshotListener<Task>>(),
      loading: false,
      employees: [],
      employeeRoles: [],
      fridgeElements: new Map(),
      deliveryElements: new Map(),
      oilElements: new Map(),
      communications: [],
      communicationResults: new Map(),
      trainings: [],
      trainingResults: new Map(),
      punchCards: new Map(),
    };
  },
  actions: {
    async setup() {
      var state = getState();

      if (
        state.userData.role != Role.Admin &&
        state.userData.role != Role.Customer &&
        state.userData.role != Role.Manager
      ) {
        return;
      }
      await ModuleManager.loadModules();

      await getStoreManager().initialize();

      this.stores = getStoreManager()
        .getStores(true)
        .filter((x) => x.active && (state.userData.role != Role.Manager || x.manager_owned));

      await getDatabaseManager().initialize();

      this.databases = getDatabaseManager().getDatabases();

      if (this.selectedStore == null) {
        this.onSelectionChanged(this.stores[0], null);
      }
    },
    getEmployeeName(ref: DocumentReference) {
      const employee = this.employees.find((x) => x.ref.id == ref.id);
      if (employee) {
        return employee.name + " " + employee.surname;
      }
      return "";
    },

    getRoleName(ref: DocumentReference) {
      if (ref == null) {
        return "No role";
      }
      var role = this.employeeRoles.find((x) => x.ref.id == ref.id);

      if (role) {
        return role.name;
      } else {
        return "Unknown role";
      }
    },
    async loadElements() {
      this.fridgeElements = new Map();

      var temperatureTasks = this.tasksOfDay
        .get(this.selectedStore)
        .items.filter((x) => x.module == ModuleEnum.Temperatures);

      for (let temperatureTask of temperatureTasks) {
        var results = await TaskManager.getTaskResults(
          temperatureTask,
          this.selectedStore
        );

        var temperatureResults: TemperatureResult[] = results
          .filter((x) => x instanceof TemperatureResult)
          .map((x) => x as TemperatureResult);

        for (let result of temperatureResults) {
          var rangeStatus = result.getElementTemperatureStateEnum();

          if (result.temperature == null) {
            continue;
          }
          if (result.element.getTemperatureDeliveryItem()) {
            this.deliveryElements.set(result.element, rangeStatus);
          } else {
            if (this.fridgeElements.has(result.element)) {
              var oldRangeStatus = this.fridgeElements.get(result.element);

              if (
                oldRangeStatus == ElementTemperatureStateEnum.OutOfRange &&
                rangeStatus == ElementTemperatureStateEnum.InRange
              ) {
                rangeStatus = ElementTemperatureStateEnum.Mixed;
              }
              if (
                oldRangeStatus == ElementTemperatureStateEnum.InRange &&
                rangeStatus == ElementTemperatureStateEnum.OutOfRange
              ) {
                rangeStatus = ElementTemperatureStateEnum.Mixed;
              }
            }
            this.fridgeElements.set(result.element, rangeStatus);
          }
        }
      }

      var oilTasks = this.tasksOfDay
        .get(this.selectedStore)
        .items.filter((x) => x.module == ModuleEnum.Oils);

      for (let task of oilTasks) {
        var results = await TaskManager.getTaskResults(
          task,
          this.selectedStore
        );

        var oilResults: OilResult[] = results
          .filter((x) => x instanceof OilResult)
          .map((x) => x as OilResult);

        for (let result of oilResults) {
          this.oilElements.set(result.element, result);
        }
      }
    },

    async loadPunchCards() {
      var year = new Date().getFullYear();

      for (let employee of this.employees.filter(
        (x) => x.active
      ) as Employee[]) {
        var punchCard = await getEmployeeManager().getEmployeePunchCard(
          employee.ref.id,
          year
        );

        if (punchCard != null) {
          punchCard.sort();
          this.punchCards.set(employee.ref.id, punchCard);
        }
      }
    },

    async loadPunchCardsForYear(year: number) {
      for (let employee of this.employees.filter(
        (x) => x.active
      ) as Employee[]) {
        var punchCard = await getEmployeeManager().getEmployeePunchCard(
          employee.ref.id,
          year
        );

        if (punchCard != null) {
          punchCard.sort();
          // Merge with existing punch cards instead of replacing
          const existingCard = this.punchCards.get(employee.ref.id);
          if (existingCard) {
            // Merge the data from both years
            for (const [storeId, dateMap] of punchCard.data.entries()) {
              if (!existingCard.data.has(storeId)) {
                existingCard.data.set(storeId, dateMap);
              } else {
                const existingDateMap = existingCard.data.get(storeId)!;
                for (const [date, entries] of dateMap.entries()) {
                  existingDateMap.set(date, entries);
                }
              }
            }
          } else {
            this.punchCards.set(employee.ref.id, punchCard);
          }
        }
      }
    },

    getEmployeePunchCard(employee: Employee): EmployeePunchCard {
      return this.punchCards.get(employee.ref.id);
    },

    deletePunchCard(employee: Employee, date: Date, entry: PunchCardEntry) {
      var key = DateUtils.formatDateAlphaSort(date);

      var punchCard = getHomeState().getEmployeePunchCard(employee);
      var data = punchCard.data.get(getHomeState().selectedStore.ref.id);

      if (!data.has(key)) {
        return;
      }

      var entries = data.get(key);

      var index = entries.indexOf(entry);

      if (index != -1) {
        entries.splice(index, 1);
      }
    },
    getPreviousPunchCard(
      employee: Employee,
      punchCardEntry: PunchCardEntry,
      date: Date
    ) {
      // Convert the date to a sortable string (e.g. "2023-03-25")
      const dateKey = DateUtils.formatDateAlphaSort(date);

      const punchCard = getHomeState().getEmployeePunchCard(employee);

      // Sort the punch card so that date-keys are in ascending order
      punchCard.sort();

      // Get the Map for the selected store
      const data = punchCard.data.get(getHomeState().selectedStore.ref.id);
      if (!data) {
        return null;
      }

      let last: PunchCardEntry | null = null;

      // Iterate through each date-key in ascending order
      for (const k of data.keys()) {
        // If k (the loop’s date) is already "greater" than the dateKey we care about,
        // stop searching
        if (k > dateKey) {
          break;
        }

        // If that day has no entries, skip it
        if (!data.get(k) || data.get(k).length === 0) {
          continue;
        }

        // Keep track of the last punch from this date
        const arr = data.get(k);
        last = arr[arr.length - 1];
      }

      return last;
    },
    addPunchCard(employee: Employee, date: Date) {
      var key = DateUtils.formatDateAlphaSort(date);

      var punchCard = getHomeState().getEmployeePunchCard(employee);

      punchCard.sort();

      if (!punchCard.data.has(getHomeState().selectedStore.ref.id)) {
        punchCard.data.set(getHomeState().selectedStore.ref.id, new Map());
      }
      var data = punchCard.data.get(getHomeState().selectedStore.ref.id);

      if (!data.has(key)) {
        data.set(key, []);
      }

      // Get existing entries for the same day
      var existingEntries = data.get(key);

      // Get the most recent punch card entry before this new one
      var previousEntry = this.getPreviousPunchCard(employee, null, date);

      // If there's a previous entry, the new entry should be the opposite
      // If no previous entry exists, default to "punch in" (true)
      var isIn = previousEntry ? !previousEntry.is_in : true;

      // Calculate timestamp based on whether previous entry is on the same day
      var timestamp: Date;

      if (existingEntries.length > 0) {
        // There are previous entries on the same day
        // Add 1 minute to the most recent entry's timestamp
        var lastEntryOnSameDay = existingEntries[existingEntries.length - 1];
        timestamp = new Date(lastEntryOnSameDay.getTimestamp().getTime() + 60 * 1000);
      } else {
        // No previous entries on the same day
        // Use current hour and minute applied to the target date
        var now = new Date();
        timestamp = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          now.getHours(),
          now.getMinutes(),
          now.getSeconds(),
          now.getMilliseconds()
        );
      }

      var entry = new PunchCardEntry(null, isIn, null, timestamp);

      data.get(key).push(entry);

      return entry;
    },
    getPunchCardEntries(employee: Employee, date: Date): PunchCardEntry[] {
      var key = DateUtils.formatDateAlphaSort(date);
      if (!this.punchCards) {
        return [];
      }
      if (!this.punchCards.has(employee.ref.id)) {
        return [];
      }

      var card: EmployeePunchCard = this.punchCards.get(employee.ref.id);

      var storeData = card.data.get(this.selectedStore.ref.id);

      if (!storeData) {
        return [];
      }

      if (!storeData.has(key)) {
        return [];
      }

      var entries = storeData.get(key);

      var results: PunchCardEntry[] = [];

      for (let entry of entries) {
        results.push(entry);
      }
      return results;
    },

    async onSelectionChanged(store: Store | null, database: Database | null) {
      this.selectedStore = store;
      this.selectedDatabase = database;

      this.loading = true;

      if (store != null) {
        if (!this.tasksOfDay.has(store)) {
          const now = new Date();

          const begin = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
          const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

          const tasksCollectionRef = collection(
            Firebase.firestore,
            `/stores/${store.ref!.id}/tasks`
          );

          const q = query(
            tasksCollectionRef,
            where("start", ">=", begin),
            where("start", "<=", end)
          );

          const listener = new SnapshotListener<Task>(
            (data: any) => Task.fromFirestore(data),
            q,
            null
          );

          await listener.ensureInit();

          this.tasksOfDay.set(store, listener);
        }

        var currentUser = getState().userData;

        this.employees = await getEmployeeManager().getEmployees(
          currentUser,
          store
        );
        this.employeeRoles = await getDatabaseManager().getEmployeeRoles(
          store.database
        );
        await getElementManager().ensureDatabaseElementListener(
          store.database.id
        );
        await getElementManager().ensureStoreElementListener(store.ref.id);
        await this.loadElements();
        this.communications =
          await getCommunicationManager().getActiveCommunications(store);

        for (let communication of this.communications) {
          var comResults =
            await getCommunicationManager().fetchCommunicationResults(
              communication
            );

          this.communicationResults.set(communication, comResults);
        }

        var user = getState().userData;

        this.trainings = await getTrainingManager().getActiveTrainings(
          user,
          store
        );

        let targetStore = user.role == Role.Customer ? store : null;

        for (let training of this.trainings) {
          var results = await getTrainingManager().fetchTrainingResults(
            training,
            targetStore
          );

          this.trainingResults.set(training, results);
        }

        await this.loadPunchCards();
      } else {
        this.communications = await getCommunicationManager().getCommunications(
          database.ref
        );

        for (let communication of this.communications) {
          var comResults =
            await getCommunicationManager().fetchCommunicationResults(
              communication
            );

          this.communicationResults.set(communication, comResults);
        }

        var user = getState().userData;

        this.trainings = await getTrainingManager().getTrainings(
          user,
          database.ref
        );

        this.trainingResults = new Map();

        for (let training of this.trainings) {
          var results = await getTrainingManager().fetchTrainingResults(
            training,
            null
          );

          this.trainingResults.set(training, results);
        }
      }
      this.loading = false;
    },
  },
});
